import { type ComponentType, lazy, useEffect, useMemo, useRef } from "react";

import Suspense from "@atlaskit/react-ufo/suspense";

import { useLoggedInHasJira } from "@/components/user-context/helpers/cohorts";
import { ClientOnly } from "@/utils/client-only";
import { experiments } from "@/utils/experiment/manifest";
import {
    EnrollmentType,
    useExperiment
} from "@/utils/experiment/use-experiment";
import { checkFeatureFlagGate } from "@/utils/feature-flags/check-feature-flag";
import { featureFlags } from "@/utils/feature-flags/feature-flag-manifest";

import type { ActivationChecklistPlacementProps } from "./activation-checklist-config";

const LazyActivationChecklistPostOfficeMount = lazy<
    ComponentType<ActivationChecklistPlacementProps>
>(() =>
    import("./activation-checklist-post-office-mount.js").then(
        ({ ActivationChecklistPostOfficeMount }) => ({
            default: ActivationChecklistPostOfficeMount
        })
    )
);

const useActivationChecklistEnrollment = () => {
    const experimentWithoutAutomaticExposure = useMemo(
        () => ({
            ...experiments.jiraWacActivationChecklist,
            options: {
                ...experiments.jiraWacActivationChecklist.options,
                fireExperimentExposure: false
            }
        }),
        []
    );
    const experiment = useExperiment(experimentWithoutAutomaticExposure);
    const exposureFired = useRef(false);

    useEffect(() => {
        if (
            !exposureFired.current &&
            experiment.type === EnrollmentType.ENROLLED
        ) {
            experiment.fireExposure();
            exposureFired.current = true;
        }
    }, [experiment]);

    return experiment;
};

export const ActivationChecklistExperimentPlacement = (
    props: ActivationChecklistPlacementProps
) => {
    const experiment = useActivationChecklistEnrollment();

    const isEligible =
        experiment.type === EnrollmentType.ENROLLED &&
        experiment.value === true;

    if (!isEligible) {
        return null;
    }

    return (
        <ClientOnly>
            <Suspense
                fallback={null}
                interactionName="wac-activation-checklist-placement"
            >
                <LazyActivationChecklistPostOfficeMount {...props} />
            </Suspense>
        </ClientOnly>
    );
};

export const ActivationChecklistPlacement = (
    props: ActivationChecklistPlacementProps
) => {
    const gateEnabled = checkFeatureFlagGate(
        featureFlags.jiraWacActivationChecklistGate
    );
    const loggedInHasJira = useLoggedInHasJira();

    if (gateEnabled !== true || loggedInHasJira !== true) {
        return null;
    }

    return <ActivationChecklistExperimentPlacement {...props} />;
};

ActivationChecklistPlacement.displayName = "ActivationChecklistPlacement";

export default ActivationChecklistPlacement;
